import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Descriptions, Tabs, Table, Tag, Typography, Statistic, Row, Col, Spinner,
  Space, Button, Tooltip, Progress, Avatar, theme, Result, Modal, Form, Input, DatePicker, EmptyState, message,
  type TagTone,
} from '../../ui';
import dayjs from 'dayjs';
import {
  Phone, MessageCircle, Mail, MapPin,
  ShoppingCart, FileText, Wrench, CalendarClock,
  Pencil, Store, FileLock, RefreshCw, ArrowLeftRight,
  Download,
} from 'lucide-react';
import { FileUploadField } from '../../components/FileUploadField';
import { LedgerStatement } from '../../components/LedgerStatement';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { request, api } from '../../api/client';
import { usePermission } from '../../store/auth';
import { RetailerFormModal } from './RetailerFormModal';
import { ChangeTerritoryModal } from '../territory/ChangeTerritoryModal';
import { displayMobile } from '../../lib/validators';
import { formatIst, formatIstDate } from '../../lib/time';

const STATUS_COLOR: Record<string, TagTone> = {
  ACTIVE: 'green', KYC_PENDING: 'gold', PROSPECT: 'blue', SUSPENDED: 'red', CLOSED: 'neutral',
};

const ORDER_COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', PLACED: 'blue', APPROVED: 'cyan', PACKED: 'gold', DISPATCHED: 'gold',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'neutral',
};

const INVOICE_COLOR: Record<string, TagTone> = {
  OPEN: 'blue', PARTIAL: 'gold', PAID: 'green', OVERDUE: 'red', CANCELLED: 'neutral',
};

const inr = (v: any) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const inr2 = (v: any) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Custom green pin for the embedded map
const retailerPin = L.divIcon({
  html: `<div style="transform:translate(-50%,-100%);">
    <svg viewBox="0 0 24 32" width="32" height="32">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z"
            fill="#1F8A4C" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="12" r="5" fill="white"/>
    </svg></div>`,
  className: '', iconSize: [32, 32], iconAnchor: [16, 32],
});

/**
 * The map sits inside AntD Tabs → Card → Col, so Leaflet initialises before that layout finishes sizing the
 * container — tiles get requested for the wrong size and never repaint (grey map with only the marker/controls).
 * Force a size recompute once the layout settles, and again on resize.
 */
function InvalidateSize() {
  const map = useMap();
  useEffect(() => {
    const fix = () => map.invalidateSize();
    const t0 = setTimeout(fix, 0);
    const t1 = setTimeout(fix, 300);
    window.addEventListener('resize', fix);
    return () => { clearTimeout(t0); clearTimeout(t1); window.removeEventListener('resize', fix); };
  }, [map]);
  return null;
}

export function RetailerDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canEdit = usePermission('RETAILER.EDIT');
  const canTransfer = usePermission('TERRITORY.TRANSFER');
  const [editOpen, setEditOpen] = useState(false);
  const [changeTerrOpen, setChangeTerrOpen] = useState(false);

  const { data: retailer, isLoading, isError } = useQuery({
    queryKey: ['retailer', id],
    queryFn: () => request<any>({ url: `/retailers/${id}` }),
  });

  const { data: ledger } = useQuery({
    queryKey: ['retailer-ledger', id],
    queryFn: () => request<any[]>({ url: `/retailers/${id}/ledger` }),
    enabled: !!id,
  });

  // Statement-of-account download — Admin / Accounts / Super-Admin only.
  const canExportLedger = usePermission('RETAILER.LEDGER.EXPORT');
  // Gate the cross-module tab queries so a viewer without that module's permission (e.g. Accounts
  // lacks COMPLAINT/VISIT) doesn't fire background 403s. Full-permission users are unaffected.
  const canViewOrders = usePermission('ORDER.VIEW');
  const canViewInvoices = usePermission('INVOICE.VIEW');
  const canViewComplaints = usePermission('COMPLAINT.VIEW');
  const canViewVisits = usePermission('VISIT.VIEW');
  const msg = message;
  const downloadLedgerPdf = async () => {
    try {
      const res = await api.get(`/retailers/${id}/ledger/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      msg.error('Failed to generate the statement PDF');
    }
  };
  const exportLedgerCsv = () => {
    const rows = ledger ?? [];
    if (!rows.length) { msg.info('No ledger entries to export'); return; }
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['Date', 'Type', 'Particulars', 'Order', 'Debit', 'Credit', 'Balance', 'Dr/Cr'];
    const body = rows.map((e: any) => {
      const bal = Number(e.balance) || 0;
      return [
        formatIst(e.createdAt),
        (e.txnType ?? '').replace(/_/g, ' '),
        [e.docNo, e.narration].filter(Boolean).join(' — '),
        e.orderNo ?? '',
        Number(e.debit) > 0 ? Number(e.debit).toFixed(2) : '',
        Number(e.credit) > 0 ? Number(e.credit).toFixed(2) : '',
        Math.abs(bal).toFixed(2),
        bal > 0.009 ? 'Dr' : bal < -0.009 ? 'Cr' : '',
      ].map(esc).join(',');
    });
    const csv = [head.map(esc).join(','), ...body].join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `statement-${retailer?.code ?? id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const { data: orders } = useQuery({
    queryKey: ['retailer-orders', id],
    queryFn: () => request<any>({ url: '/orders', params: { retailerId: id, pageSize: 50 } }),
    enabled: !!id && canViewOrders,
  });

  const { data: invoices } = useQuery({
    queryKey: ['retailer-invoices', id],
    queryFn: () => request<any[]>({ url: '/invoices', params: { retailerId: id } }),
    enabled: !!id && canViewInvoices,
  });

  const { data: complaints } = useQuery({
    queryKey: ['retailer-complaints', id],
    queryFn: () => request<any[]>({ url: '/complaints', params: { retailerId: id } }),
    enabled: !!id && canViewComplaints,
  });

  const { data: visits } = useQuery({
    queryKey: ['retailer-visits', id],
    queryFn: () => request<any[]>({ url: '/visits', params: { retailerId: id } }),
    enabled: !!id && canViewVisits,
  });

  const { data: agreements } = useQuery({
    queryKey: ['retailer-agreements', id],
    queryFn: () => request<any[]>({ url: `/retailers/${id}/agreements` }),
    enabled: !!id,
  });
  const [renewOpen, setRenewOpen] = useState(false);

  // Resolve territory + assigned-sales-person ids → names (gated; fall back to a
  // row name field or #id when the viewer lacks the master-data permission).
  const canViewTerritories = usePermission('TERRITORY.VIEW');
  const canViewSalesUsers = usePermission('USER.VIEW');
  const { data: territories } = useQuery({
    queryKey: ['retailer-territories'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: !!id && canViewTerritories,
  });
  const { data: spUsers } = useQuery({
    queryKey: ['retailer-sp-users'],
    queryFn: () => request<any>({ url: '/users', params: { pageSize: 500 } }),
    enabled: !!id && canViewSalesUsers,
  });

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !retailer) {
    return (
      <Result
        status="error"
        title="Couldn't load this retailer"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/people?tab=retailers')}>Back to retailers</Button>}
      />
    );
  }

  // Credit summary — kept simple and intuitive.
  //   outstanding > 0  → they OWE you that much (due)
  //   outstanding < 0  → they're IN CREDIT (paid in advance)
  //   available        = credit limit − outstanding (how much they can still order)
  const limit = Number(retailer.creditLimit) || 0;
  const out = Number(retailer.outstandingBalance) || 0;
  const owed = Math.max(0, out);       // amount currently due
  const advance = Math.max(0, -out);   // overpaid / on-account credit
  const available = limit - out;
  const utilPct = limit > 0 ? Math.max(0, Math.min(100, Math.round((owed / limit) * 100))) : 0;
  const utilColor = utilPct >= 90 ? '#D9363E' : utilPct >= 60 ? '#E7B22C' : '#52C41A';
  const lat = Number(retailer.latitude);
  const lng = Number(retailer.longitude);
  const territoryLabel = (() => {
    const t = (territories ?? []).find((x: any) => String(x.id) === String(retailer.territoryId));
    return t ? `${t.code} · ${t.name}` : (retailer.territoryName ?? (retailer.territoryId ? `#${retailer.territoryId}` : '—'));
  })();
  const spLabel = (() => {
    const u = (spUsers?.data ?? []).find((x: any) => String(x.id) === String(retailer.assignedSalesPersonId));
    return u?.fullName ?? (retailer.assignedSalesPersonName ?? (retailer.assignedSalesPersonId ? `#${retailer.assignedSalesPersonId}` : '—'));
  })();
  const waNumber = (retailer.whatsappNumber ?? retailer.mobile ?? '').replace('+', '');

  return (
    <div>
      {/* Header */}
      <Card className="mb-4">
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Avatar
              size={56}
              style={{ background: '#1F8A4C', fontSize: 22, fontWeight: 700 }}
              fallback={<Store size={22} />}
            />
          </Col>
          <Col flex="auto" style={{ minWidth: 0 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {retailer.companyName}
            </Typography.Title>
            <Space size={8} wrap style={{ marginTop: 4 }}>
              <Tag>{retailer.code}</Tag>
              <Tag tone={STATUS_COLOR[retailer.status] ?? 'neutral'}>{retailer.status}</Tag>
              <span style={{ color: token.colorTextSecondary }}>
                {retailer.contactPerson} · {retailer.city}, {retailer.stateCode}
              </span>
            </Space>
          </Col>
          <Col flex="none">
            <Space size={6}>
              <Tooltip label="Call">
                <Button size="sm" href={`tel:${displayMobile(retailer.mobile)}`} icon={<Phone size={14} />} aria-label="Call" />
              </Tooltip>
              <Tooltip label="WhatsApp">
                <Button size="sm" href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener" icon={<MessageCircle size={14} />} aria-label="WhatsApp" />
              </Tooltip>
              {retailer.email && (
                <Tooltip label="Email">
                  <Button size="sm" href={`mailto:${retailer.email}`} icon={<Mail size={14} />} aria-label="Email" />
                </Tooltip>
              )}
              {Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) && (
                <Tooltip label="View on Google Maps">
                  <Button
                    size="sm"
                    href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                    target="_blank"
                    rel="noopener"
                    icon={<MapPin size={14} />}
                    aria-label="View on Google Maps"
                  />
                </Tooltip>
              )}
              <Button size="sm" variant="primary" icon={<ShoppingCart size={14} />} onClick={() => navigate(`/orders/new?retailerId=${id}`)}>
                Place Order
              </Button>
              {canTransfer && (
                <Tooltip label="Change territory">
                  <Button size="sm" icon={<ArrowLeftRight size={14} />} aria-label="Change territory" onClick={() => setChangeTerrOpen(true)} />
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip label="Edit">
                  <Button size="sm" icon={<Pencil size={14} />} aria-label="Edit" onClick={() => setEditOpen(true)} />
                </Tooltip>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Credit summary — Limit, what they owe (or advance), and what's left */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Credit Limit" value={limit} formatter={(v) => inr2(v)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            {advance > 0 ? (
              <Statistic
                title="Advance (in credit)"
                value={advance}
                formatter={(v) => inr2(v)}
                valueStyle={{ color: '#52C41A' }}
              />
            ) : (
              <Statistic
                title="Outstanding (due)"
                value={owed}
                formatter={(v) => inr2(v)}
                valueStyle={{ color: owed > 0 ? '#D9363E' : token.colorTextSecondary }}
              />
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Available to order"
              value={available}
              formatter={(v) => inr2(v)}
              valueStyle={{ color: available > 0 ? '#52C41A' : '#D9363E' }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <div style={{ color: token.colorTextSecondary, fontSize: 14 }}>Credit used</div>
            <div style={{ marginTop: 8 }}>
              <Progress percent={utilPct} strokeColor={utilColor} />
            </div>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginTop: 4 }}>
              {retailer.creditPeriodDays}-day credit period
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Tabs
        items={[
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <Row gutter={16}>
                <Col xs={24} lg={14}>
                  <Card title="Profile">
                    <Descriptions bordered column={2} size="small">
                      <Descriptions.Item label="Contact">{retailer.contactPerson}</Descriptions.Item>
                      <Descriptions.Item label="Mobile">{displayMobile(retailer.mobile)}</Descriptions.Item>
                      <Descriptions.Item label="WhatsApp">{displayMobile(retailer.whatsappNumber) || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Email">{retailer.email ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="GST">{retailer.gstNumber ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="PAN">{retailer.panNumber ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Agri License No.">{retailer.agriLicenseNo ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Discount">
                        {retailer.discountType === 'GENERAL'
                          ? <Tag tone="blue">General · {Number(retailer.generalDiscountPct ?? 0)}% fixed</Tag>
                          : <Tag>Customize (per order)</Tag>}
                      </Descriptions.Item>
                      <Descriptions.Item label="Territory">{territoryLabel}</Descriptions.Item>
                      <Descriptions.Item label="Assigned SP">{spLabel}</Descriptions.Item>
                      <Descriptions.Item label="Address" span={2}>
                        {retailer.addressLine1}
                        {retailer.addressLine2 ? `, ${retailer.addressLine2}` : ''}, {retailer.city}, {retailer.stateCode} – {retailer.pincode}
                      </Descriptions.Item>
                      <Descriptions.Item label="Lat / Long">{retailer.latitude}, {retailer.longitude}</Descriptions.Item>
                      <Descriptions.Item label="Onboarded">
                        {formatIstDate(retailer.onboardedAt)}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
                <Col xs={24} lg={10}>
                  <Card title="Location" bodyClassName="p-0">
                    {Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0) ? (
                      <MapContainer center={[lat, lng]} zoom={13} style={{ height: 320, width: '100%' }} scrollWheelZoom={false}>
                        <InvalidateSize />
                        <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
                        <Marker position={[lat, lng]} icon={retailerPin}>
                          <Popup>{retailer.companyName}</Popup>
                        </Marker>
                      </MapContainer>
                    ) : (
                      <div style={{ padding: 24, color: token.colorTextTertiary }}>No GPS coordinates on record.</div>
                    )}
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'orders',
            label: <span><ShoppingCart size={16} /> Orders ({orders?.meta?.total ?? orders?.data?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={orders?.data ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(o: any) => ({ onClick: () => navigate(`/orders/${o.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Order No', dataIndex: 'orderNo' },
                  { title: 'Date', dataIndex: 'orderDate', render: (v) => formatIst(v) },
                  { title: 'Total', dataIndex: 'grandTotal', render: (v) => inr2(v), align: 'right' as const },
                  { title: 'Status', dataIndex: 'status', render: (s) => <Tag tone={ORDER_COLOR[s]}>{s}</Tag> },
                ]}
              />
            ),
          },
          {
            key: 'invoices',
            label: <span><FileText size={16} /> Invoices ({invoices?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={invoices ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(i) => ({ onClick: () => navigate(`/billing/invoices/${i.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'Invoice', dataIndex: 'invoiceNo' },
                  { title: 'Date', dataIndex: 'invoiceDate' },
                  { title: 'Due', dataIndex: 'dueDate' },
                  { title: 'Total', dataIndex: 'grandTotal', render: (v) => inr2(v), align: 'right' as const },
                  { title: 'Paid', dataIndex: 'paidAmt', render: (v) => inr2(v), align: 'right' as const },
                  {
                    title: 'Balance', dataIndex: 'balanceAmt',
                    render: (v) => <b style={{ color: Number(v) > 0 ? '#D9363E' : '#52C41A' }}>{inr2(v)}</b>,
                    align: 'right' as const,
                  },
                  { title: 'Status', dataIndex: 'status', render: (s) => <Tag tone={INVOICE_COLOR[s]}>{s}</Tag> },
                ]}
              />
            ),
          },
          {
            key: 'ledger',
            label: 'Ledger',
            children: (
              <div>
                {canExportLedger && (
                  <div className="mb-2 flex justify-end">
                    <Space>
                      <Button icon={<Download size={16} />} onClick={exportLedgerCsv}>Export CSV</Button>
                      <Button icon={<FileText size={16} />} onClick={downloadLedgerPdf}>Download PDF</Button>
                    </Space>
                  </div>
                )}
                <LedgerStatement data={ledger ?? []} />
              </div>
            ),
          },
          {
            key: 'visits',
            label: <span><CalendarClock size={16} /> Visits ({visits?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={visits ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                columns={[
                  { title: 'Visit No', dataIndex: 'visitNo' },
                  { title: 'Check-in', dataIndex: 'checkInAt', render: (v) => formatIst(v) },
                  { title: 'Duration', dataIndex: 'durationMin', render: (v) => v ? `${v} min` : '—' },
                  {
                    title: 'Geofence', dataIndex: 'checkInWithinGeofence',
                    render: (v) => v ? <Tag tone="green">In zone</Tag> : <Tag tone="red">Outside</Tag>,
                  },
                  {
                    title: 'Order placed', dataIndex: 'orderPlaced',
                    render: (v) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
                  },
                  { title: 'Score', dataIndex: 'effectivenessScore' },
                ]}
              />
            ),
          },
          {
            key: 'complaints',
            label: <span><Wrench size={16} /> Complaints ({complaints?.length ?? 0})</span>,
            children: (
              <Table
                rowKey="id"
                dataSource={complaints ?? []}
                pagination={{ pageSize: 20 }}
                size="small"
                onRow={(c) => ({ onClick: () => navigate(`/complaints/${c.id}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: 'No', dataIndex: 'complaintNo' },
                  { title: 'Category', dataIndex: 'category' },
                  { title: 'Severity', dataIndex: 'severity' },
                  { title: 'Status', dataIndex: 'status' },
                  { title: 'Raised', dataIndex: 'createdAt', render: (v) => formatIstDate(v) },
                  {
                    title: 'SLA Due', dataIndex: 'slaDueAt',
                    render: (v) => {
                      if (!v) return '—';
                      const due = new Date(v);
                      const breach = due < new Date();
                      return <span style={{ color: breach ? '#D9363E' : undefined }}>{formatIst(v)}</span>;
                    },
                  },
                ]}
              />
            ),
          },
          {
            key: 'agreement',
            label: <span><FileLock size={16} /> Agreement ({agreements?.length ?? 0})</span>,
            children: (
              <>
                <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <Space size="large" wrap>
                    <Statistic title="Current expiry"
                      formatter={() => {
                        const s = agreementTone(retailer.agreementExpiry);
                        return retailer.agreementExpiry
                          ? <Tag tone={s.color} style={{ fontSize: 14 }}>{formatIstDate(retailer.agreementExpiry)} · {s.label}</Tag>
                          : <Tag>No agreement on file</Tag>;
                      }} />
                    {retailer.agreementUrl && (
                      <Button href={retailer.agreementUrl} target="_blank" rel="noreferrer" icon={<FileText size={16} />}>View current copy</Button>
                    )}
                  </Space>
                  {canEdit && <Button variant="primary" icon={<RefreshCw size={16} />} onClick={() => setRenewOpen(true)}>Renew agreement</Button>}
                </Space>
                <Table
                  rowKey="id"
                  dataSource={agreements ?? []}
                  pagination={{ pageSize: 20 }}
                  size="small"
                  locale={{ emptyText: <EmptyState title="No agreements recorded yet" /> }}
                  columns={[
                    { title: 'Agreement date', dataIndex: 'agreementDate', render: (v) => v ? formatIstDate(v) : '—' },
                    { title: 'Valid till', dataIndex: 'expiryDate', render: (v) => v ? formatIstDate(v) : '—' },
                    { title: 'Notes', dataIndex: 'notes', render: (v) => v ?? '—' },
                    { title: 'Recorded', dataIndex: 'createdAt', render: (v) => formatIst(v) },
                    {
                      title: 'Copy', dataIndex: 'agreementUrl',
                      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">Open</a> : '—',
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />

      {canEdit && (
        <RetailerFormModal open={editOpen} onClose={() => setEditOpen(false)} record={retailer} />
      )}
      {canTransfer && (
        <ChangeTerritoryModal open={changeTerrOpen} onClose={() => setChangeTerrOpen(false)} customerType="RETAILER" customer={retailer} />
      )}
      {canEdit && <RenewAgreementModal open={renewOpen} onClose={() => setRenewOpen(false)} retailerId={id} />}
    </div>
  );
}

// Same red/amber tone logic as the list, for the detail header tag.
function agreementTone(expiry?: string | null): { label: string; color: TagTone } {
  if (!expiry) return { label: 'None', color: 'neutral' };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiry); exp.setHours(0, 0, 0, 0);
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { label: `expired ${-days}d ago`, color: 'red' };
  if (days <= 30) return { label: `expires in ${days}d`, color: 'gold' };
  return { label: 'active', color: 'green' };
}

function RenewAgreementModal({ open, onClose, retailerId }: { open: boolean; onClose: () => void; retailerId: string }) {
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: `/retailers/${retailerId}/agreements`, method: 'POST',
      data: {
        agreementUrl: vals.agreementUrl,
        agreementDate: vals.agreementDate ? vals.agreementDate.format('YYYY-MM-DD') : undefined,
        expiryDate: vals.expiryDate ? vals.expiryDate.format('YYYY-MM-DD') : undefined,
        notes: vals.notes,
      },
    }),
    onSuccess: () => {
      message.success('Agreement renewed');
      qc.invalidateQueries({ queryKey: ['retailer-agreements', retailerId] });
      qc.invalidateQueries({ queryKey: ['retailer', retailerId] });
      qc.invalidateQueries({ queryKey: ['retailers'] });
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to renew'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Renew dealer agreement"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save agreement</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)} initialValues={{ agreementDate: dayjs() }}>
        <Form.Item name="agreementUrl" label="Agreement copy" extra="Upload the newly signed agreement (PDF/photo).">
          <FileUploadField category="attachment" buttonLabel="Upload agreement" preview={false} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="agreementDate" label="Agreement date" rules={[{ required: true, message: 'Pick the agreement date' }]}>
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="expiryDate" label="Valid till (expiry)" rules={[{ required: true, message: 'Pick the expiry date' }]}>
              <DatePicker className="w-full" format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={2} placeholder="e.g. 1-year renewal, revised terms…" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
